import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { Role } from '@app/core/interfaces/c5/role';
import { SearchResult } from '@app/core/interfaces/search-result';
import { SearchType } from '@app/core/interfaces/search-type';
import { SearchRepository } from '@app/core/repositories/search.repository';
import { RouterService } from '@app/routing/router.service';
import { loggerFor } from '@app/utils/logger';
import { wrapError } from '@app/utils/rxjs';
import { SearchResultViewModel } from '@business/search/models/search-result-view-model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private logger = loggerFor('SearchService');

  constructor(
    private routerService: RouterService,
    private searchRepository: SearchRepository,
    private authenticationService: AuthenticationService
  ) {}

  searchEnterprise(query: string, type?: SearchType[]): Observable<SearchResultViewModel> {
    this.logger.info(`Executing global search on enterprise level, query=${query}`);

    return this.searchRepository.searchEnterprise(query, type).pipe(
      map((result) => this.mapResponse(query, result)),
      wrapError('search-service:search-enterprise')
    );
  }

  searchReseller(query: string, type?: SearchType[]): Observable<SearchResultViewModel> {
    let { serviceProviderId } = this.routerService.currentRouteData.params;

    if (serviceProviderId == null) {
      serviceProviderId = this.authenticationService.authUser.serviceProviderName;
    }

    this.logger.info(
      `Executing global search on reseller level, query=${query}, resellerId=${serviceProviderId}`
    );

    return this.searchRepository.searchReseller(query, serviceProviderId, type).pipe(
      map((result) => this.mapResponse(query, result)),
      wrapError('search-service:search-reseller')
    );
  }

  private mapResponse(query: string, searchResult: SearchResult): SearchResultViewModel {
    // TODO: Someday support more services, but now limit response to the ones we support
    searchResult.services = searchResult.services.filter(
      (service) =>
        service.role !== Role.VOICE_MAIL &&
        service.role !== Role.AD_HOC_CONF &&
        service.role !== Role.CALL_RECORDING
    );

    const reNum = query
      .replace('%', '*')
      .replace(/[^0-9?*]/g, '')
      .replace('*', '.*')
      .replace('?', '.');

    for (const user of searchResult.users) {
      user.numbers = this.filterNumbers(user, reNum);
    }

    for (const user of searchResult.services) {
      user.numbers = this.filterNumbers(user, reNum);
    }

    const empty =
      searchResult.devices.length === 0 &&
      searchResult.domains.length === 0 &&
      searchResult.services.length === 0 &&
      searchResult.users.length === 0;

    return {
      empty,
      searchResult,
    };
  }

  private filterNumbers(user, reNum) {
    return user.numbers.filter((phoneNumber) => {
      let result = phoneNumber.main === true;

      if (result === false && reNum !== '') {
        const e164Match = phoneNumber.e164 !== undefined && phoneNumber.e164.match(reNum) !== undefined;
        const internalMatch =
          phoneNumber.internal !== undefined && phoneNumber.internal.match(reNum) !== undefined;

        result = e164Match || internalMatch;
      }

      return result;
    });
  }
}
